import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Alert,
  AlertTitle,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  TextField,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import DeleteOutlineIconButton from "components/common/DeleteOutlineIconButton";
import EditIcon from "@mui/icons-material/Edit";
import RateReviewIcon from "@mui/icons-material/RateReview";
import DownloadIcon from '@mui/icons-material/Download';
import { IconExternalLink } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import theme from 'lib/theme';
import { formatDate } from 'utils/helpers';
import { Pickup } from 'types/pickup';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { PickupStatus } from './PickupStatus';
import { useTerminology } from 'hooks/useTerminology';
import { getOrganizationType, isDrmaOrganization, isSyneticOrganization } from 'lib/organizationType';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import useRequestPickupChange from 'hooks/pickups/useRequestPickupChange';
import { useAlert } from 'hooks/alerts/useAlert';

interface PickupTableProps {
  pickups: Pickup[];
  errorMessage: null;
  isLoading: boolean;
  getPickups: () => void;
  hasNextPage: boolean;
  deletePickup: (pickupUuid: string) => Promise<void>;
  incrementCurrentPage: () => void;
}

function pickupSearchHaystack(pickup: Pickup) {
  const loc = pickup.location;
  return [
    pickup.orderNumber != null ? String(pickup.orderNumber) : '',
    pickup.contactName,
    pickup.contactEmail,
    pickup.contactPhone,
    pickup.name,
    pickup.state,
    pickup.comments,
    loc?.address,
    loc?.address2,
    loc?.city,
    loc?.state,
    loc?.zipcode,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

export default function PickupCards({
  pickups,
  errorMessage,
  isLoading,
  deletePickup,
  getPickups,
}: PickupTableProps) {
  const terminology = useTerminology();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const isSynetic = isSyneticOrganization();
  const { requestChange } = useRequestPickupChange();
  const { alertSuccess, alertError } = useAlert();
  const [requestChangePickup, setRequestChangePickup] = useState<Pickup | null>(null);
  const [requestChangeNotes, setRequestChangeNotes] = useState('');
  const [requestChangeSubmitting, setRequestChangeSubmitting] = useState(false);
  const [requestChangeError, setRequestChangeError] = useState<string | null>(null);

  const visiblePickups = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return pickups;
    return pickups.filter((p) => pickupSearchHaystack(p).includes(q));
  }, [pickups, searchQuery]);

  useEffect(() => {
    if (!isSynetic || isLoading) return;
    const uuid = searchParams.get('requestChange');
    if (!uuid) return;

    const match = pickups.find(
      (p) => p.uuid === uuid && p.state === 'pending_confirmation'
    );
    if (match) {
      setRequestChangePickup(match);
      setRequestChangeNotes('');
      setRequestChangeError(null);
    }

    const next = new URLSearchParams(searchParams);
    next.delete('requestChange');
    setSearchParams(next, { replace: true });
  }, [isSynetic, isLoading, pickups, searchParams, setSearchParams]);

  const openRequestChange = (pickup: Pickup) => {
    setRequestChangePickup(pickup);
    setRequestChangeNotes('');
    setRequestChangeError(null);
  };

  const closeRequestChange = () => {
    if (requestChangeSubmitting) return;
    setRequestChangePickup(null);
    setRequestChangeNotes('');
    setRequestChangeError(null);
  };

  const submitRequestChange = async () => {
    if (!requestChangePickup?.uuid) return;
    const notes = requestChangeNotes.trim();
    if (!notes) {
      setRequestChangeError('Please describe the change you need.');
      return;
    }

    setRequestChangeSubmitting(true);
    setRequestChangeError(null);
    try {
      await requestChange(requestChangePickup.uuid, notes);
      alertSuccess('Your change request was submitted. We will follow up within one business day.');
      setRequestChangePickup(null);
      setRequestChangeNotes('');
    } catch {
      alertError('Unable to submit your change request. Please try again.');
      setRequestChangeError('Unable to submit your change request. Please try again.');
    } finally {
      setRequestChangeSubmitting(false);
    }
  };

  if (errorMessage) {
    return (
      <Box
        sx={{
          mt: 6,
          border: '1px solid',
          borderColor: theme.palette.divider,
          background: 'white',
          minHeight: '500px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Alert severity='error'>
          <AlertTitle>Error</AlertTitle>
          {errorMessage ||
            `There was a problem loading your ${terminology.pickups}.`}
        </Alert>
      </Box>
    );
  }

  const isEmpty = pickups?.length === 0;

  if (!isLoading && isEmpty && !searchQuery.trim()) {
    return (
      <Box
        sx={{
          mt: 6,
          p: 4,
          borderRadius: '12px',
          boxShadow: 2,
          background: 'white',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant='h5' fontWeight={'bold'} sx={{ mt: 2 }}>
          No {terminology.pickups} scheduled yet!
        </Typography>
        <Typography
          mt={1}
          maxWidth={'400px'}
          textAlign='center'
          sx={{ color: '#555' }}
        >
          Ready to start? Schedule your first {terminology.pickup} with the
          button below.
        </Typography>
        <Button
          component={RouterLink}
          variant='contained'
          size='large'
          to='/pickups/new'
          sx={{ mt: 4 }}
        >
          Schedule {terminology.Pickup}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <TextField
        size="small"
        placeholder={`Search ${terminology.pickups}…`}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{
          mb: 2,
          minWidth: { xs: '100%', sm: 280 },
          maxWidth: 400,
          '& .MuiOutlinedInput-root': { bgcolor: '#ffffff' },
        }}
      />
      {visiblePickups.length === 0 ? (
        isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              py: 6,
              px: 2,
              textAlign: 'center',
              border: '1px solid',
              borderColor: theme.palette.divider,
              borderRadius: 1,
              bgcolor: 'background.paper',
            }}
          >
            <Typography color="text.secondary">
              {pickups.length === 0
                ? `No ${terminology.pickups} loaded.`
                : `No ${terminology.pickups} match your search.`}
            </Typography>
          </Box>
        )
      ) : (
        <Grid container spacing={2}>
          {visiblePickups.map((pickup) => (
            <Grid item xs={12} sm={6} md={4} key={pickup.id}>
              <PickupCard
                pickup={pickup}
                deletePickup={deletePickup}
                getPickups={getPickups}
                showRequestChange={isSynetic}
                onRequestChange={openRequestChange}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog
        open={Boolean(requestChangePickup)}
        onClose={closeRequestChange}
        fullWidth
        maxWidth="sm"
        aria-labelledby="request-change-dialog-title"
      >
        <DialogTitle id="request-change-dialog-title">
          Request a change
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Describe the change you need for{' '}
            {terminology.pickup}{' '}
            {requestChangePickup?.orderNumber
              ? `#${requestChangePickup.orderNumber}`
              : ''}
            . Your account rep will follow up within one business day.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={4}
            label="Details"
            value={requestChangeNotes}
            onChange={(e) => setRequestChangeNotes(e.target.value)}
            error={Boolean(requestChangeError)}
            helperText={requestChangeError || ' '}
            disabled={requestChangeSubmitting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRequestChange} disabled={requestChangeSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={submitRequestChange}
            variant="contained"
            disabled={requestChangeSubmitting || !requestChangeNotes.trim()}
          >
            {requestChangeSubmitting ? 'Submitting…' : 'Submit request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function PickupCard({
  pickup,
  deletePickup,
  getPickups,
  showRequestChange,
  onRequestChange,
}: {
  pickup: Pickup;
  deletePickup: Function;
  getPickups: Function;
  showRequestChange: boolean;
  onRequestChange: (pickup: Pickup) => void;
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const terminology = useTerminology();
  const isAssettech = getOrganizationType() === 'assettech';
  const showTrackingLink =
    isDrmaOrganization() &&
    Boolean(pickup.trackingEnabled) &&
    ['scheduled', 'processing'].includes(pickup.state || '');

  const handleDelete = async () => {
    await deletePickup(pickup.id);
    getPickups();
    setConfirmOpen(false);
  };

  const downloadDocument = (doc: any) => {
    // Handle both camelCase (converted) and snake_case (raw) formats
    const secureUrl = doc.secureUrl || doc.secure_url;
    const url = doc.url;
    const fileType = doc.fileType || doc.file_type;
    
    // Create a temporary link to download the document
    const link = document.createElement('a');
    link.href = secureUrl || url;
    link.download = `${doc.name}.${fileType}`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Card sx={{ width: '100%', minHeight: '225px', position: 'relative' }}>
      <CardContent>
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            display: 'flex',
            gap: 1,
          }}
        >
          {pickup.state === 'pending_confirmation' && (
            <>
              {showRequestChange && (
                <Tooltip title="Request change">
                  <IconButton
                    size="small"
                    aria-label="Request change"
                    onClick={() => onRequestChange(pickup)}
                  >
                    <RateReviewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <IconButton
                size='small'
                component={RouterLink}
                to={`/pickups/${pickup.uuid}/edit`}
              >
                <EditIcon fontSize='small' />
              </IconButton>
              <DeleteOutlineIconButton aria-label={`Delete ${terminology.pickup}`} onClick={() => setConfirmOpen(true)} />
            </>
          )}
        </Box>
        <Typography variant='h6' component='div'>
          {(pickup.scheduledDate && formatDate(pickup.scheduledDate)) ||
            'Select Date'}
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          {terminology.Pickup} Number: {pickup.orderNumber}
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          Address:{' '}
          {(pickup.location && pickup.location.address) || 'Add address'}{' '}
          {(pickup.location && pickup.location.address2) || ''} <br></br>
          {(pickup.location && pickup.location.city) || ''}
          {pickup.location && pickup.location.state
            ? `, ${pickup.location.state}`
            : ''}{' '}
          {pickup.location && pickup.location.zipcode
            ? ` ${pickup.location.zipcode}`
            : ''}
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          Point of Contact: {pickup.contactName || 'Add contact'}
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          Status: <PickupStatus pickup={pickup} />
        </Typography>
        {showTrackingLink && (
          <Button
            component={RouterLink}
            to={`/pickups/${pickup.uuid}/tracking`}
            size="small"
            variant="outlined"
            startIcon={<MyLocationIcon fontSize="small" />}
            sx={{ mt: 2 }}
          >
            Track shipment
          </Button>
        )}
        {['complete', 'Completed', 'Processed'].includes(pickup.state || '') && (
          <Box sx={{ pt: 2 }}>
            {/* Dynamic Documents Section */}
            {pickup.documents && pickup.documents.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ mb: 1, fontWeight: 'medium' }}
                >
                  Documents:
                </Typography>
                {pickup.documents.map((doc) => (
                  <Box
                    key={doc.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mt: 1,
                      cursor: 'pointer',
                    }}
                    onClick={() => downloadDocument(doc)}
                  >
                    <DownloadIcon
                      fontSize='small'
                      sx={{ mr: 1, color: 'primary.main' }}
                    />
                    <Typography variant='body2' component='span'>
                      {doc.name}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {isAssettech ? (
              <>
                <Typography
                  variant='body2'
                  component='div'
                  sx={{ display: 'flex', alignItems: 'center', mt: 1 }}
                >
                  <Box mr={1}>
                    <IconExternalLink />
                  </Box>
                  <Link
                    component={RouterLink}
                    to={`/pickups/${pickup.uuid}/cab`}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    View CAB
                  </Link>
                </Typography>
                <Typography
                  variant='body2'
                  component='div'
                  sx={{ display: 'flex', alignItems: 'center', mt: 1 }}
                >
                  <Box mr={1}>
                    <IconExternalLink />
                  </Box>
                  <Link
                    component={RouterLink}
                    to={`/pickups/${pickup.uuid}/sustainability`}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    View Sustainability Report
                  </Link>
                </Typography>
              </>
            ) : (
              <Typography
                variant='body2'
                component='div'
                sx={{ display: 'flex', alignItems: 'center', mt: 1 }}
              >
                <Box mr={1}>
                  <IconExternalLink />
                </Box>
                <Link
                  component={RouterLink}
                  to={`/assets?filter[]=state,is,retired&filter[]=pickup_id,is_any_of,${pickup.id}`}
                >
                  View all Assets
                </Link>
              </Typography>
            )}
          </Box>
        )}
      </CardContent>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
      >
        <DialogTitle id='alert-dialog-title'>{`Delete ${terminology.Pickup}`}</DialogTitle>
        <DialogContent>
          <DialogContentText id='alert-dialog-description'>
            Are you sure you want to delete this {terminology.pickup}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
